import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import TeamBadge from '../TeamBadge/TeamBadge';
import { TRANSLATION_KEYS } from '../cards/GroupStage/constants';
import { useTranslation } from 'hooks/useTranslation';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { normalizeText } from '../../../hooks/useMundialMetrics/utils/normalizeText';
import LiveTag from '../../../components/LiveTag';
import './styles.scss';
import { getRegion } from 'utils/Region';

export default function MatchCard({
  time,
  teamA,
  teamB,
  shortNameA,
  shortNameB,
  flagUrlA,
  flagUrlB,
  stadium,
  status,
  score,
  highlight,
  className,
  lineRight,
  lineLeft,
  lineWidth,
  lineLeftWidth,
  lineRightWidth,
  abbreviateTeamNamesOnMobile,
  noFlagPlaceholderSrc,
  matchMinute,
  isBroadcastAvailable,
  onClick,
}) {
 const sports_worldcup_phase2_gracenote_config = useSelector(state => {
     if (state.metadata.sports_worldcup_phase2_gracenote_config) {
       const config = JSON.parse(state.metadata.sports_worldcup_phase2_gracenote_config);
       const region = getRegion();
       if (config[region]) {
         return config[region].enabled;
       }
       return config.default?.enabled ?? false;
     }
     return false;
   });

  const style = {
    ...(lineWidth ? { '--match-line-width': `${lineWidth}px` } : {}),
    ...(lineLeftWidth ? { '--match-line-left-width': `${lineLeftWidth}px` } : {}),
    ...(lineRightWidth ? { '--match-line-right-width': `${lineRightWidth}px` } : {}),
  };

  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(
    () => translationMapper(TRANSLATION_KEYS),
    [],
  );
  const translations = useSelector(state => state.translations);

  const renderLiveLabel = () => {
    const minuteRaw = matchMinute;
    const minuteStr =
      minuteRaw === 0 || minuteRaw === '0'
        ? '0'
        : minuteRaw != null && String(minuteRaw).trim() !== ''
          ? String(minuteRaw).trim()
          : null;

    const suffix = minuteStr ? `${minuteStr}'` : null;

    if (isBroadcastAvailable) {
      return (
        <>
          <LiveTag />
          {suffix && <span className="match-card__live-minute"> {suffix}</span>}
        </>
      );
    }

    if (!minuteStr) {
      return translatedTexts['IN_PROGRESS'];
    }

    const minutoAMinutoBase = translatedTexts['IN_PROGRESS'] ||
      getTranslationEmptyOrNullKey('minuto_a_minuto', translations) ||
      getTranslationEmptyOrNullKey('texto_minuto_a_minuto', translations) ||
      'Minuto a minuto';

    return `${minutoAMinutoBase} ${suffix}`;
  };

  const renderTimeContent = () => {
    if (status === 'COMPLETED') return translatedTexts['COMPLETED'];
    if (status === 'IN_PROGRESS') return renderLiveLabel();
    if (typeof time !== 'string') return time;

    if (time.includes('\n')) {
      const parts = time.split('\n');
      return parts.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </React.Fragment>
      ));
    }
    return time;
  };

  const matchName = teamA && teamB ? normalizeText(`${teamA} vs ${teamB}`) : undefined;

 

  return (
    <div
      className={`match-card ${highlight ? 'match-card--highlight' : ''} ${status === 'IN_PROGRESS' ? 'match-card--live' : ''
        } ${lineRight ? 'match-card--line-right' : ''} ${lineLeft ? 'match-card--line-left' : ''
        } ${className || ''}`.trim()}
      data-role="mundial2026-match-card"
      data-match-name={matchName}
      onClick={sports_worldcup_phase2_gracenote_config ? onClick : () => {}}
      onKeyDown={
        onClick
          ? e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(e);
            }
          }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={Object.keys(style).length ? style : undefined}
    >
      <div className="match-card__time">{renderTimeContent()}</div>
      <div className="match-card__teams">
        <TeamBadge
          name={teamA}
          shortName={shortNameA}
          flagUrl={flagUrlA}
          abbreviateTeamNamesOnMobile={abbreviateTeamNamesOnMobile}
          noFlagPlaceholderSrc={noFlagPlaceholderSrc}
        />
        {(status === 'COMPLETED' || status === 'IN_PROGRESS') && score ? (
          <span className="match-card__score">
            {score.home} - {score.away}
          </span>
        ) : (
          <span className="match-card__vs">VS</span>
        )}
        <TeamBadge
          name={teamB}
          shortName={shortNameB}
          flagUrl={flagUrlB}
          abbreviateTeamNamesOnMobile={abbreviateTeamNamesOnMobile}
          noFlagPlaceholderSrc={noFlagPlaceholderSrc}
        />
      </div>
      {stadium && <div className="match-card__stadium">{stadium}</div>}
    </div>
  );
}
